import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const emptyMember = { member_number: '', name: '', last_name: '', address: '', postal_code: '', city: '', phone: '', email: '', joined_date: '', birth_year: '', partner_name: '', partner_last_name: '' };

const fullName = (m) => [m.name, m.last_name].filter(Boolean).join(' ');

export default function Members() {
  const { apiFetch } = useAuth();
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMember);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');

  const load = () => {
    apiFetch('/api/members').then(setMembers).catch(err => setError(err.message));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyMember });
    setError('');
    setShowModal(true);
  };

  const openEdit = (member) => {
    setEditing(member.id);
    setForm(member);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editing) {
        await apiFetch(`/api/members/${editing}`, { method: 'PUT', body: form });
      } else {
        await apiFetch('/api/members', { method: 'POST', body: form });
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette dette medlem?')) return;
    try {
      await apiFetch(`/api/members/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem('token');
    const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
    const res = await fetch(`${API_BASE}/api/members/pdf`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Medlemsliste.pdf';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field) => {
    if (sortBy !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const filtered = members
    .filter(m =>
      fullName(m).toLowerCase().includes(search.toLowerCase()) ||
      (m.email && m.email.toLowerCase().includes(search.toLowerCase())) ||
      (m.address && m.address.toLowerCase().includes(search.toLowerCase())) ||
      (m.city && m.city.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'last_name':
          valA = (a.last_name || '').toLowerCase() + ' ' + (a.name || '').toLowerCase();
          valB = (b.last_name || '').toLowerCase() + ' ' + (b.name || '').toLowerCase();
          break;
        case 'name':
          valA = (a.name || '').toLowerCase() + ' ' + (a.last_name || '').toLowerCase();
          valB = (b.name || '').toLowerCase() + ' ' + (b.last_name || '').toLowerCase();
          break;
        case 'address':
          valA = [a.address, a.postal_code, a.city].filter(Boolean).join(', ').toLowerCase();
          valB = [b.address, b.postal_code, b.city].filter(Boolean).join(', ').toLowerCase();
          break;
        case 'birth_year':
          valA = a.birth_year || 0;
          valB = b.birth_year || 0;
          break;
        default:
          valA = (a.last_name || '').toLowerCase();
          valB = (b.last_name || '').toLowerCase();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div>
      <div className="page-header">
        <h1>Medlemmer ({members.length})</h1>
        <div style={{display: 'flex', gap: '0.5rem'}}>
          <button className="btn btn-secondary" onClick={downloadPdf}>Download PDF</button>
          <button className="btn btn-primary" onClick={openNew}>+ Nyt medlem</button>
        </div>
      </div>

      <div className="card" style={{marginBottom: '1rem'}}>
        <input className="form-control" placeholder="Søg efter navn, adresse eller email..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {error && !showModal && <div className="alert alert-error" style={{marginBottom: '1rem'}}>{error}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort('name')}>Fornavn(e){sortIndicator('name')}</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort('last_name')}>Efternavn{sortIndicator('last_name')}</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort('address')}>Adresse{sortIndicator('address')}</th>
              <th>Telefon</th>
              <th>Email</th>
              <th style={{cursor: 'pointer'}} onClick={() => handleSort('birth_year')}>Fødselsår{sortIndicator('birth_year')}</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="8" className="text-center">Ingen medlemmer fundet</td></tr>
            ) : filtered.map(m => (
              <tr key={m.id}>
                <td>{m.name}</td>
                <td style={{fontWeight: 500}}>{m.last_name || ''}</td>
                <td>{[m.address, m.postal_code, m.city].filter(Boolean).join(', ')}</td>
                <td>{m.phone}</td>
                <td>{m.email}</td>
                <td>{m.birth_year || ''}</td>
                <td><span className={`badge ${m.is_active ? 'badge-active' : 'badge-inactive'}`}>
                  {m.is_active ? 'Aktiv' : 'Inaktiv'}
                </span></td>
                <td className="text-right" style={{whiteSpace: 'nowrap'}}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(m)} style={{marginRight: '0.25rem'}}>Rediger</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(m.id)}>Slet</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Rediger medlem' : 'Nyt medlem'}</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Fornavn(e)</label>
                  <input className="form-control" value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})} required
                    placeholder="f.eks. Jens Erik" />
                </div>
                <div className="form-group">
                  <label>Efternavn</label>
                  <input className="form-control" value={form.last_name || ''}
                    onChange={e => setForm({...form, last_name: e.target.value})}
                    placeholder="f.eks. Hansen" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Partner fornavn(e)</label>
                  <input className="form-control" value={form.partner_name || ''}
                    onChange={e => setForm({...form, partner_name: e.target.value})}
                    placeholder="f.eks. Inge" />
                </div>
                <div className="form-group">
                  <label>Partner efternavn</label>
                  <input className="form-control" value={form.partner_last_name || ''}
                    onChange={e => setForm({...form, partner_last_name: e.target.value})}
                    placeholder="f.eks. Hansen" />
                </div>
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input className="form-control" value={form.address || ''}
                  onChange={e => setForm({...form, address: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Postnr.</label>
                  <input className="form-control" value={form.postal_code || ''}
                    onChange={e => setForm({...form, postal_code: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>By</label>
                  <input className="form-control" value={form.city || ''}
                    onChange={e => setForm({...form, city: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Telefon</label>
                  <input className="form-control" value={form.phone || ''}
                    onChange={e => setForm({...form, phone: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" type="email" value={form.email || ''}
                    onChange={e => setForm({...form, email: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fødselsår</label>
                  <input className="form-control" type="number" value={form.birth_year || ''}
                    onChange={e => setForm({...form, birth_year: e.target.value ? parseInt(e.target.value) : ''})}
                    placeholder="F.eks. 1952" min="1900" max={new Date().getFullYear()} />
                </div>
                <div className="form-group">
                  <label>Indmeldt dato</label>
                  <input className="form-control" type="date" value={form.joined_date || ''}
                    onChange={e => setForm({...form, joined_date: e.target.value})} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuller</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Gem' : 'Opret'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
